import React from "react";
import { motion } from "framer-motion";
import { ASSETS, PRICE } from "../data/content";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden flex items-center pt-28 pb-20"
        >
            {/* Background portrait */}
            <div className="absolute inset-0 z-0">
                <img
                    src={ASSETS.viktor}
                    alt="Viktor Varek"
                    className="absolute inset-0 w-full h-full object-cover object-[60%_30%] opacity-55"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#030303] via-[#030303]/85 to-[#030303]/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]/60" />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        background:
                            "radial-gradient(circle at 75% 50%, rgba(122,0,0,0.35), transparent 55%)",
                    }}
                />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-7"
                >
                    <div className="flex items-center gap-3 mb-5">
                        <div
                            className="flex items-center gap-1"
                            data-testid="hero-rating-stars"
                            aria-label="4.9 out of 5 stars"
                        >
                            {[0, 1, 2, 3, 4].map((i) => (
                                <svg
                                    key={i}
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="#990000"
                                    aria-hidden="true"
                                >
                                    <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                                </svg>
                            ))}
                        </div>
                        <span
                            className="text-[11px] tracking-[0.22em] uppercase text-neutral-400"
                            data-testid="hero-rating-text"
                        >
                            <span className="text-[#f4f4f0]">4.9/5</span> · 4,350+
                            readers across the world
                        </span>
                    </div>
                    <div className="flex items-center gap-3 mb-8">
                        <span className="inline-block w-1.5 h-1.5 bg-[#7A0000] ember" />
                        <span className="overline">
                            A Private Edition · Vol. I
                        </span>
                    </div>
                    <h1
                        className="font-serif-display text-[44px] sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight text-[#f4f4f0]"
                        data-testid="hero-title"
                    >
                        The <span className="italic text-[#990000]">Varek</span>{" "}
                        Code.
                        <br />
                        <span className="text-neutral-400 italic font-light">
                            Quiet rules of ultimate power.
                        </span>
                    </h1>
                    <p
                        className="mt-8 max-w-xl text-neutral-400 text-base sm:text-lg leading-relaxed"
                        data-testid="hero-subtitle"
                    >
                        The frameworks they never taught you — to command
                        respect, orchestrate perception, and become the man no
                        room can ignore. Five phases. One transformation. No
                        retreat.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                        <a
                            href="#pricing"
                            data-testid="hero-cta-primary"
                            className="btn-primary"
                        >
                            <span>Claim the Code · {PRICE.discounted}</span>
                            <span aria-hidden>→</span>
                        </a>
                        <a
                            href="#phases"
                            data-testid="hero-cta-secondary"
                            className="btn-ghost"
                        >
                            Preview the Five Phases
                        </a>
                    </div>

                    <div className="mt-12 flex items-center gap-8 text-[11px] tracking-[0.24em] uppercase text-neutral-500">
                        <div>
                            <span className="line-through text-neutral-600 mr-2">
                                {PRICE.original}
                            </span>
                            <span className="text-[#990000]">
                                {PRICE.discountLabel} — launch window
                            </span>
                        </div>
                        <div className="hidden sm:block w-px h-4 bg-white/10" />
                        <div className="hidden sm:block">Instant access</div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                        duration: 1.4,
                        delay: 0.3,
                        ease: [0.16, 1, 0.3, 1],
                    }}
                    className="lg:col-span-5 flex justify-center lg:justify-end relative"
                >
                    <div className="relative">
                        <div
                            className="absolute -inset-10 blur-3xl opacity-60"
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(122,0,0,0.5), transparent 60%)",
                            }}
                        />
                        <img
                            src={ASSETS.book}
                            alt="The Varek Code book cover"
                            className="relative w-[280px] sm:w-[360px] lg:w-[420px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.95)]"
                            style={{
                                transform: "perspective(1400px) rotateY(-10deg) rotateX(2deg)",
                            }}
                            data-testid="hero-book-image"
                        />
                    </div>
                </motion.div>
            </div>

            {/* Bottom scroll cue */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-neutral-500 z-10">
                Scroll · Read · Act
            </div>
        </section>
    );
}
